/**
 * CalendarEventModal — dumb form component for create/edit + read-only view.
 *
 * - mode='create': blank form, summary required
 * - mode='view': read-only details + Edit/Delete buttons
 * - mode='edit': editable form (partial update)
 *
 * State is local (useState — 5 fields, no need for useReducer/react-hook-form).
 * Parent owns the mutation + cache invalidation. metaJson is validated client-
 * side before allowing submit (BE call avoided if JSON syntax error).
 */

import React from 'react';
import { X, Edit2, Trash2, Save, Loader2 } from 'lucide-react';

import type { CalendarEvent, CalendarEventCreate, CalendarEventUpdate } from '../api/calendar';

export type ModalMode = 'create' | 'view' | 'edit';

interface CalendarEventModalProps {
  open: boolean;
  mode: ModalMode;
  event: CalendarEvent | null;
  busy?: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onModeChange: (mode: ModalMode) => void;
  onSubmitCreate: (body: CalendarEventCreate) => void;
  onSubmitUpdate: (body: CalendarEventUpdate) => void;
  onDelete: () => void;
}

interface FormState {
  summary: string;
  startDt: string;
  endDt: string;
  description: string;
  color: string;
  metaJson: string;
}

const COLOR_RE = /^#[0-9a-fA-F]{6}$/;
const RFC3339_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}([+-]\d{2}:\d{2}|Z)$/;

function emptyForm(): FormState {
  // sensible default — today 09:00 + 10:00 KST
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return {
    summary: '',
    startDt: `${date}T09:00:00+09:00`,
    endDt: `${date}T10:00:00+09:00`,
    description: '',
    color: '',
    metaJson: '',
  };
}

function formFromEvent(ev: CalendarEvent): FormState {
  return {
    summary: ev.summary ?? '',
    startDt: ev.startDt ?? '',
    endDt: ev.endDt ?? '',
    description: ev.description ?? '',
    color: ev.color ?? '',
    metaJson: ev.metadata && Object.keys(ev.metadata).length > 0
      ? JSON.stringify(ev.metadata, null, 2)
      : '',
  };
}

const CalendarEventModal: React.FC<CalendarEventModalProps> = ({
  open,
  mode,
  event,
  busy,
  errorMessage,
  onClose,
  onModeChange,
  onSubmitCreate,
  onSubmitUpdate,
  onDelete,
}) => {
  const [form, setForm] = React.useState<FormState>(emptyForm);
  const [localError, setLocalError] = React.useState<string | null>(null);

  // Sync form when event changes (open with different event)
  React.useEffect(() => {
    if (!open) return;
    setForm(event && mode !== 'create' ? formFromEvent(event) : emptyForm());
    setLocalError(null);
  }, [open, event, mode]);

  // Autofocus first input on open
  const summaryRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (open && mode !== 'view') summaryRef.current?.focus();
  }, [open, mode]);

  if (!open) return null;

  const isView = mode === 'view';
  const isEdit = mode === 'edit';
  const isCreate = mode === 'create';

  function validate(): { ok: true; meta: Record<string, unknown> | undefined } | { ok: false; reason: string } {
    if (!form.summary.trim()) return { ok: false, reason: 'summary 필수' };
    if (!RFC3339_RE.test(form.startDt)) {
      return { ok: false, reason: 'startDt 는 RFC3339 (예: 2026-05-18T10:00:00+09:00) 형식이어야 합니다' };
    }
    if (form.endDt && !RFC3339_RE.test(form.endDt)) {
      return { ok: false, reason: 'endDt RFC3339 형식 오류' };
    }
    if (form.color && !COLOR_RE.test(form.color)) {
      return { ok: false, reason: 'color 는 #RRGGBB 형식이어야 합니다' };
    }
    let meta: Record<string, unknown> | undefined;
    if (form.metaJson.trim()) {
      try {
        meta = JSON.parse(form.metaJson) as Record<string, unknown>;
      } catch (e) {
        return { ok: false, reason: `metaJson JSON 오류: ${(e as Error).message}` };
      }
    }
    return { ok: true, meta };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v = validate();
    if (!v.ok) {
      setLocalError(v.reason);
      return;
    }
    setLocalError(null);
    // BE accepts metaJson as a string (JSON.stringify it back)
    const metaStr = v.meta !== undefined ? JSON.stringify(v.meta) : undefined;
    const base = {
      summary: form.summary.trim(),
      startDt: form.startDt,
      endDt: form.endDt || undefined,
      description: form.description || undefined,
      color: form.color || undefined,
      metaJson: metaStr,
    };
    if (isCreate) {
      onSubmitCreate(base);
    } else {
      // Update — only diff would be ideal; for simplicity send everything,
      // BE PATCH is partial-update friendly.
      onSubmitUpdate(base);
    }
  }

  const update = <K extends keyof FormState>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((f) => ({ ...f, [key]: e.target.value }));
    };

  const title = isCreate ? '새 이벤트' : isEdit ? '이벤트 편집' : '이벤트 상세';

  return (
    <div
      className="border-t border-slate-200 bg-white"
      role="region"
      aria-label={title}
      data-testid="cal-modal"
    >
      <div className="overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button
            data-testid="cal-modal-close"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 px-4 py-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">제목 *</span>
            <input
              ref={summaryRef}
              data-testid="cal-modal-summary"
              type="text"
              value={form.summary}
              onChange={update('summary')}
              readOnly={isView}
              required
              maxLength={200}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm read-only:bg-slate-50 read-only:text-slate-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              시작 (RFC3339, +offset)
            </span>
            <input
              data-testid="cal-modal-start"
              type="text"
              value={form.startDt}
              onChange={update('startDt')}
              readOnly={isView}
              required
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs read-only:bg-slate-50 read-only:text-slate-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">종료 (선택)</span>
            <input
              data-testid="cal-modal-end"
              type="text"
              value={form.endDt}
              onChange={update('endDt')}
              readOnly={isView}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs read-only:bg-slate-50 read-only:text-slate-600"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">설명</span>
            <textarea
              data-testid="cal-modal-desc"
              value={form.description}
              onChange={update('description')}
              readOnly={isView}
              rows={2}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm read-only:bg-slate-50 read-only:text-slate-600"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-slate-600">색상 (#RRGGBB)</span>
              <input
                data-testid="cal-modal-color"
                type="text"
                value={form.color}
                onChange={update('color')}
                readOnly={isView}
                placeholder="#2196F3"
                className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs read-only:bg-slate-50 read-only:text-slate-600"
              />
            </label>
            <div>
              <span className="mb-1 block text-xs font-medium text-slate-600">유형</span>
              <input
                value={(event?.calendarType ?? 'TRAINING_PLAN') as string}
                readOnly
                className="w-full rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-600"
              />
              <p className="mt-1 text-[10px] text-slate-400">BE 가 TRAINING_PLAN 강제</p>
            </div>
          </div>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-600">metaJson (JSON object)</span>
            <textarea
              data-testid="cal-modal-meta"
              value={form.metaJson}
              onChange={update('metaJson')}
              readOnly={isView}
              rows={3}
              placeholder='{"sessionType": "interval", "sets": 5}'
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs read-only:bg-slate-50 read-only:text-slate-600"
            />
          </label>

          {(localError || errorMessage) && (
            <p
              data-testid="cal-modal-error"
              className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
            >
              {localError ?? errorMessage}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
            {isView && (
              <>
                <button
                  type="button"
                  data-testid="cal-modal-delete"
                  onClick={onDelete}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded border border-red-200 px-3 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 size={14} />
                  삭제
                </button>
                <button
                  type="button"
                  data-testid="cal-modal-edit-btn"
                  onClick={() => onModeChange('edit')}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 px-3 py-1 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <Edit2 size={14} />
                  편집
                </button>
              </>
            )}
            {(isCreate || isEdit) && (
              <button
                type="submit"
                data-testid="cal-modal-save"
                disabled={busy}
                className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1 text-xs text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                저장
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalendarEventModal;
